/**
 * The icon registry keys of a project's application, by its `AppType`
 * (`iconsApi.icon`): its logo, and the glyph of one of its instances (an
 * environment's address) under `<logo>-instance`.
 *
 * THE LOGO, IN ONE PLACE (proposition B, 2026-10-25): the slug of
 * components/ui/icons/appLogos.ts its `AppType` names in its
 * `waadoo.ovh/logo` annotation; else its own name when it is such a slug
 * (`wagtail`); else its name all the same, for a module that registers a
 * glyph of its own under it — no icon when none does. The core registers
 * every slug and its `-instance` key (iconBundle.tsx); an app type module
 * may register its own glyph under them, which wins (`plugins/odoo`). The
 * legacy type's name when the type is not known yet, as `ProjectToDelete`
 * reads it.
 *
 * The annotation is read from the type's descriptor (`loadAppType`, the
 * cache shared with `useAppTitle`): until it is read, or when it cannot be,
 * the type's name decides.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { isAppLogoSlug, type AppLogoSlug } from '../ui/icons/appLogos';
import { LEGACY_APP_TYPE } from './projectIdentity';
import { loadAppType } from './templateRoles';

/**
 * The annotation an `AppType` — or a template (createPage/cardPresentation.ts)
 * — names its logo by: a slug of appLogos.ts. An unknown value counts as none.
 */
export const LOGO_ANNOTATION = 'waadoo.ovh/logo';

/** The annotations of an `AppType` (`AppTypeSummary.annotations`). */
export type AppTypeAnnotations = Readonly<Record<string, string>>;

/**
 * The slug of the logo of `appType` (see above); undefined when neither its
 * annotation nor its name is one.
 */
export function appLogoSlug(
  appType: string | undefined,
  annotations?: AppTypeAnnotations,
): AppLogoSlug | undefined {
  const annotated = annotations?.[LOGO_ANNOTATION];
  if (annotated !== undefined && isAppLogoSlug(annotated)) return annotated;
  const name = appType ?? LEGACY_APP_TYPE;
  return isAppLogoSlug(name) ? name : undefined;
}

/** The key of the application's logo. */
export function appLogoKey(
  appType: string | undefined,
  annotations?: AppTypeAnnotations,
): string {
  return appLogoSlug(appType, annotations) ?? appType ?? LEGACY_APP_TYPE;
}

/** The key of the glyph of one of its instances (an environment's address). */
export function appInstanceKey(
  appType: string | undefined,
  annotations?: AppTypeAnnotations,
): string {
  return `${appLogoKey(appType, annotations)}-instance`;
}

/** What a view needs of an application's logo. */
export interface AppLogoKeys {
  /** `appLogoKey`. */
  logo: string;
  /** `appInstanceKey`. */
  instance: string;
  /**
   * `appLogoSlug`: the logo is one of appLogos.ts — a tile then takes its
   * colour (`logoStyle`, `.waadoo-home-project-tile[data-app-logo]`).
   */
  slug?: AppLogoSlug;
}

export function appLogoKeys(
  appType: string | undefined,
  annotations?: AppTypeAnnotations,
): AppLogoKeys {
  const slug = appLogoSlug(appType, annotations);
  return {
    logo: appLogoKey(appType, annotations),
    instance: appInstanceKey(appType, annotations),
    ...(slug ? { slug } : {}),
  };
}

/**
 * `appLogoKeys` of `appType`, with its annotations once its descriptor is
 * read — the type's name alone meanwhile, and for good when it cannot be.
 * Never the previous type's answer when `appType` changes.
 */
export function useAppLogoKeys(appType: string | undefined): AppLogoKeys {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [loaded, setLoaded] = useState<{
    name: string;
    annotations: AppTypeAnnotations;
  }>();

  useEffect(() => {
    if (appType === undefined) return undefined;
    let active = true;
    loadAppType({ fetchApi, discoveryApi }, appType).then(
      found => {
        if (active && found) {
          setLoaded({ name: appType, annotations: found.annotations });
        }
      },
      () => {
        // Unreadable descriptor: the type's name decides (header).
      },
    );
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi, appType]);

  const annotations =
    loaded && loaded.name === appType ? loaded.annotations : undefined;
  // A new object only when the answer changes, for the callers' memos.
  return useMemo(
    () => appLogoKeys(appType, annotations),
    [appType, annotations],
  );
}
